import { useEffect, useRef } from 'react';
import { useQuery, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useSmartPolling } from '@/hooks/useSmartPolling';
import { POLLING_INTERVALS } from '@/lib/performanceConfig';
import { documentosDaEtapa, filaQualidadeOptions } from './useQualidadeInbox';

export type ConversationStatus =
  | 'nova'
  | 'em_atendimento'
  | 'aguardando_cliente'
  | 'aguardando_interno'
  | 'convertida'
  | 'finalizada'
  | 'arquivada';

export interface Conversation {
  id: string;
  telefone: string;
  wa_id: string | null;
  campanha_id: string | null;
  destinatario_id: string | null;
  lead_id: string | null;
  /** CNPJ do cliente, resolvido por telefone no webhook. Já vinha no select;
   *  faltava na interface. */
  cliente_cnpj?: string | null;
  cliente_fonte?: string | null;
  liberada_consultor?: boolean | null;
  owner_user_id: string | null;
  status: ConversationStatus;
  tags: string[];
  unread_count: number;
  last_message_preview: string | null;
  last_message_at: string | null;
  last_message_status: string | null;
  last_message_direction: string | null;
  last_inbound_at: string | null;
  last_outbound_at: string | null;
  first_inbound_at: string | null;
  finalizada_em: string | null;
  created_at: string;
  contact_name: string | null;
  /** Foto de perfil do contato no WhatsApp. Preenchido por conexoes Evolution
   *  (360Dialog nao fornece — fica null e o avatar mostra icone neutro). */
  contact_avatar_url?: string | null;
  // Janela 24h oficial (vinda da Meta via webhook 360dialog)
  meta_window_expires_at?: string | null;
  bee_pode_responder?: boolean | null;
  bee_pode_responder_em?: string | null;
  meta_conversation_id?: string | null;
  meta_conversation_origin?: string | null;
  // Joined fields
  owner_nome?: string | null;
  /** O dono do cliente (quem atende é owner_user_id). */
  consultor_comercial_id?: string | null;
  owner_foto?: string | null;
  owner_equipe?: string | null;
  campanha_nome?: string | null;
  campanha_template?: string | null;
  /** True quando a campanha de origem tem agente IA ativo (auto-resposta). */
  agente_ia_ativo?: boolean;
  /** True quando a IsabellA respondeu por último — ela atende ESTA conversa. */
  agente_ia_atendendo?: boolean;
  ia_pausada_em?: string | null;
  /** Nome do agente IA (ex: "IsabellA") quando agente_ia_ativo = true. */
  agente_ia_nome?: string | null;
  /** ID do agente IA da campanha de origem — usado para filtrar a aba "Agentes IA". */
  agente_ia_id?: string | null;
  destinatario_nome?: string | null;
  destinatario_razao_social?: string | null;
  destinatario_cnpj?: string | null;
  connection_id?: string | null;
  connection_nome?: string | null;
  connection_telefone?: string | null;
  /** Provider da conexão. Importante: janela 24h só se aplica a 360dialog. */
  connection_provider?: string | null;
  /** Canal da última mensagem: oficial (Cloud API) | nao_oficial (Baileys) | evolution.
   *  Fica na CONVERSA e não na conexão porque uma linha do Bee vive nos dois canais. */
  canal_atual?: string | null;
  /** Consultor que solicitou atendimento (via Passar Atendimento -> Atendimento).
   *  Conversa-alvo na conexão oficial fica marcada até alguém assumir. */
  solicitacao_atendimento_de_user_id?: string | null;
  solicitacao_atendimento_em?: string | null;
  /** Nome do consultor solicitante (joined). Usado pelo chip amarelo do header. */
  solicitante_atendimento_nome?: string | null;
  /** Quando setado, esta conversa é um grupo interno (chat empresa/equipe/custom).
   *  ChatWindow trata diferente (não chama edge externa, painel mostra membros). */
  chat_grupo_id?: string | null;
}

// Tabs base + 'emkt-rf' (legado, será migrado pra 'tag:EMKT-RF') +
// padrão dinâmico 'tag:NOME' que filtra conversas por tag (entrou pra
// suportar visões dinâmicas geradas pelas regras de roteamento).
// 'ia-atendendo' = owner_user_id NULL + campanha tem agente_ativo
// (IA em atendimento ativo, ninguem precisa entrar).
export type ConvTab =
  | `qualidade:${string}`
  | 'todas'
  | 'novas'
  | 'minhas'
  | 'aguardando'
  | 'finalizadas'
  | 'emkt-rf'
  | 'ia-atendendo'
  | 'disparos'
  /** A FILA DE CADASTRO: número sem documento e sem lead. Todos veem. */
  | 'sem-cadastro'
  /** SEM DONO: ninguém assumiu. Quem pegar primeiro é o dono. */
  | 'sem-dono'
  | `tag:${string}`;

export function useConversations(
  tab: ConvTab = 'todas',
  searchTerm = '',
  /**
   * Toggle do líder pra forçar "ver só as minhas" (igual escopo de consultor).
   * Quando true, ignora visaoEquipe — filtra por owner_user_id=me em todas
   * as abas. Default false mantém comportamento legado pra outros callers.
   */
  forcarPropriasApenas = false,
  /**
   * ⛔ (Douglas 21/08) OS FILTROS PRECISAM RODAR NO SERVIDOR.
   * "avalia muito bem estes filtros, eu acho que e tudo quebrado, nunca da o certo"
   *
   * Ele estava certo e da' pra medir: a lista carrega 200 conversas e existem
   * 18.857. Tag, conexao e equipe eram filtradas no NAVEGADOR, em cima dessas
   * 200 — ou seja, filtrar por uma conexao devolvia so' as conversas dela que
   * por acaso estivessem entre as 200 mais recentes DO SISTEMA INTEIRO.
   * Conexao movimentada mas sem mensagem hoje: zero resultado, sem aviso.
   *
   * Agora entram na consulta. O filtro client-side continua depois, de graca,
   * porque a lista tambem recebe conversas mescladas da busca e do agente.
   */
  filtros?: {
    tags?: string[];
    connectionIds?: string[];
    /** ids dos donos — vem das equipes escolhidas, ja resolvidas pela RPC */
    ownerIds?: string[];
    /** "So Master": campanhas ia_only. Sao 1 hoje — cabe folgado na URL. */
    campanhaIds?: string[];
    /** Recorte por PEDIDO (quadro · status · tipo de lead · documento).
     *  Esses quatro não vivem em whatsapp_conversations: dependem de juntar
     *  cliente e pedido. `useConversasPorPedido` resolve QUAIS conversas casam
     *  e manda os ids para cá — a consulta da lista roda a cada digitação na
     *  busca e não pode carregar um join desses. */
    conversationIds?: string[];
    /** Competência (mes_trabalho) dentro da etapa do ciclo de Qualidade.
     *  Só vale nas abas `qualidade:` — e filtra ENCOLHENDO a lista de
     *  documentos, ou seja, com menos requisições, não mais. */
    competencias?: string[];
    /** Semáforo da verificação de eSIM dentro da etapa. */
    estadosEsim?: string[];
    /** O OLHO (Douglas 26/09/2026): só cliente DONO CARTEIRA. O servidor filtra
     *  pela mesma função dos contadores (cabeçalho x-inbox-escopo). */
    soCarteira?: boolean;
    /** O BKO NAS SUAS FILAS (Douglas 28/09/2026): só conversa de cliente com
     *  item nos quadros da função dele. Mesmo cabeçalho, outro escopo. */
    soFilas?: boolean;
    /** O BOTÃO CPF/CNPJ (Douglas 28/09/2026): só um segmento. O servidor filtra
     *  pela mesma função dos contadores (cabeçalho x-inbox-segmento). */
    segmento?: 'PF' | 'PJ' | null;
  },
  /**
   * ⛔ PAUSA ENQUANTO O OPERADOR ESCOLHE (Douglas, 17/09/2026: "filtro fica
   * disparando sozinho").
   *
   * A lista faz polling (PESO A) e o Inbox recebe dezenas de mensagens por
   * minuto. Com o modal de filtros ABERTO, cada volta do polling refazia a
   * consulta filtrada: os números dos botões pulavam, o "Após filtros" mudava
   * sozinho e o rodapé ficava piscando "calculando" enquanto ele ainda estava
   * decidindo o que marcar.
   *
   * Enquanto o modal está aberto, a lista congela. Nada se perde: ao fechar, o
   * polling volta e a primeira volta traz tudo que chegou no meio.
   */
  pausarAtualizacao = false,
) {
  const { profile } = useAuth();
  const queryClient = useQueryClient();

  // Roles com visão ampla (veem TUDO): liderança comercial + qualidade.
  // Qualidade auditoria todo o atendimento — qualquer pessoa do time de
  // qualidade enxerga as conversas inteiras do CRM.
  const ROLES_VISAO_AMPLA = ['master', 'gerente', 'diretor', 'qualidade'];
  // (Douglas 03/08) BKO_PORTABILIDADE vê tudo, igual à qualidade: a bola delas
  // (SMS Anatel, eSIM, agenda de porta) chega por QUALQUER conexão, e sem a
  // conversa inteira não dá pra ajustar nem falar com o cliente. O backend já
  // libera (rls.middleware, 15/07) — faltava a tela. É por FUNÇÃO, não por role:
  // BKO_CONTRATO e BKO_INPUT seguem vendo só as suas.
  const FUNCOES_VISAO_AMPLA = ['BKO_PORTABILIDADE'];
  // Roles/funções que enxergam toda a equipe (supervisor, coordenador, líder de equipe)
  const isLider = !!profile?.role && ['supervisor', 'coordenador'].includes(profile.role);
  const isFuncaoLider = typeof (profile as any)?.funcao === 'string'
    && /^(SUPERVISOR|COORDENADOR)/i.test(((profile as any).funcao as string).trim());
  const visaoAmpla = ROLES_VISAO_AMPLA.includes(profile?.role || '')
    || FUNCOES_VISAO_AMPLA.includes(String(profile?.funcao || '').trim().toUpperCase());
  const visaoEquipe = (visaoAmpla || isLider || isFuncaoLider) && !forcarPropriasApenas;

  const { refetchInterval: smartInterval } = useSmartPolling({
    weight: 'A',
    baseInterval: POLLING_INTERVALS.PESO_A,
    enabled: !!profile?.id,
  });

  // Chave estavel dos filtros: ordenada, senao a mesma selecao em ordem
  // diferente vira cache novo e a tela pisca sem motivo.
  const filtrosKey = [
    (filtros?.tags ?? []).slice().sort().join(','),
    (filtros?.connectionIds ?? []).slice().sort().join(','),
    (filtros?.ownerIds ?? []).slice().sort().join(','),
    (filtros?.campanhaIds ?? []).slice().sort().join(','),
    (filtros?.conversationIds ?? []).slice().sort().join(','),
    (filtros?.competencias ?? []).slice().sort().join(','),
    (filtros?.estadosEsim ?? []).slice().sort().join(','),
    filtros?.soFilas ? 'filas' : filtros?.soCarteira ? 'carteira' : '',
    filtros?.segmento ?? '',
  ].join('|');

  const { data: paginas, isLoading, isFetching, refetch, hasNextPage, fetchNextPage, isFetchingNextPage } = useInfiniteQuery({
    // forcarPropriasApenas integra a queryKey pra disparar refetch quando
    // o líder clica no toggle "ver só as minhas" no header. Sem isso, o
    // React Query servia o cache da query antiga (vista-equipe) e o filtro
    // novo nunca rodava.
    queryKey: ['whatsapp-conversations', tab, profile?.id, forcarPropriasApenas, filtrosKey, 'paginas'],
    initialPageParam: 0,
    getNextPageParam: (ultima: Conversation[], paginas: Conversation[][]) =>
      (tab === 'todas' || tab === 'novas') && ultima.filter(c => !c.chat_grupo_id).length >= 200 ? paginas.length : undefined,
    queryFn: async ({ pageParam }) => {
      // ⛔ EXCEÇÃO 'disparos' (Douglas 04/09): essa aba não é fila de trabalho,
      // é AUDITORIA — ele clica no chip pra conferir tudo que entrou por
      // disparo de e-mail. Com o teto de 200 o chip dizia 328 e a lista só
      // renderizava 200: 128 clientes sumiam e nenhum scroll trazia de volta
      // ("está faltando cliente? isso não está quebrado?"). Como a ordem é
      // `last_message_at` desc, os cortados eram justamente os mais antigos —
      // os que estão parados há mais tempo sem ninguém falar.
      // Fica FORA da cadeia do builder de propósito: ternário dentro do
      // `.limit()` fazia o TS estourar a profundidade (TS2589) na linha do
      // `.from()` e apagar a checagem do trecho inteiro.
      const teto = tab === 'disparos' ? 1000 : 200;
      const regrasAviso = tab === 'disparos' ? (await supabase
        .from('whatsapp_regra_roteamento' as any).select('pool_destino')
        .eq('ativa', true).not('tipo_aviso', 'is', null)).data : [];
      const construirQuery = () => {
      let query = supabase
        .from('whatsapp_conversations')
        // ⚡ Performance: select explícito com somente as colunas usadas pelo card
        // (antes era `select *` + 4 joins aninhados — 30+ colunas, query lenta)
        .select(`
          id, telefone, wa_id, destinatario_id, lead_id, cliente_cnpj, cliente_fonte, liberada_consultor, chat_grupo_id,
          owner_user_id, consultor_comercial_id, connection_id, campanha_id, status, tags, unread_count, agente_ia_atendendo, agente_ia_responsavel_id, ia_pausada_em,
          last_message_preview, last_message_at, last_message_status,
          last_message_direction, last_inbound_at, last_outbound_at,
          first_inbound_at, finalizada_em, created_at, contact_name, contact_avatar_url,
          meta_window_expires_at, meta_conversation_id, meta_conversation_origin, bee_pode_responder, bee_pode_responder_em,
          solicitacao_atendimento_de_user_id, solicitacao_atendimento_em, canal_atual,
          owner:owner_user_id(nome, foto_url, equipe:equipe_id(nome)),
          destinatario:destinatario_id(nome, razao_social, cnpj),
          connection:connection_id(nome, telefone, provider, agente_ativo, agente_ia_id, agente:agente_ia_id(id, nome)),
          solicitante_atendimento:solicitacao_atendimento_de_user_id(nome)
        `)
        // Limit 200 — equilíbrio entre cobertura e performance dos joins
        // aninhados (owner/campanha/destinatario/connection). Antes era 500
        // mas com 4 joins ficava lento. 200 cobre 99% do uso (operador rola
        // até ~50, raramente mais que isso) sem travar a UI.
        // O teto varia por aba — ver `teto`, logo acima do builder.
        .range(pageParam * teto, (pageParam + 1) * teto - 1);

      // Grupos internos: TODO grupo que o usuário participa entra na lista
      // como conversa normal (Douglas 04/07: "igual WhatsApp" — grupo sobe
      // quando tem mensagem, com preview e badge). A seção GruposChatSection
      // vira só um atalho/diretório.
      // Grupos internos entram por uma SEGUNDA query simples (mais abaixo) e
      // são mesclados na lista — o or(chat_grupo_id.in.(...)) do gateway é
      // frágil (histórico de parser 20/06) e escondia os grupos (Douglas 04/07).
      query = query.is('chat_grupo_id', null);

      // (2026-05-23) Aba "Novas" passa a ordenar por last_inbound_at DESC
      // (mais recente em cima) — comportamento de WhatsApp normal, que é
      // o que o operador espera ver. Antes era FIFO (first_inbound_at ASC)
      // pra priorizar quem esperava mais tempo, mas com 300+ conversas
      // "novas" históricas vindas do sync (Empresarial Prime Oficial),
      // mensagens novas do dia ficavam soterradas na posição 250+ e o
      // operador não achava o cliente que respondeu agora.
      //
      // Limit subiu 100 → 500 pra dar margem mesmo com volume grande.
      if (tab === 'novas') {
        query = query.order('last_inbound_at', { ascending: false, nullsFirst: false });
      } else {
        // CRÍTICO: nullsFirst:false força conversas SEM last_message_at pro
        // FIM da lista. Sem isso, Postgres DESC coloca NULLs no topo —
        // resultado: LIDs vazias (sem msg) ficavam acima de Fabio Canuto.
        query = query.order('last_message_at', { ascending: false, nullsFirst: false });
      }
      query = query.order('id', { ascending: true });

      // Filtro por aba
      if (tab === 'sem-cadastro') {
        /* ⛔ A FILA DE CADASTRO (Douglas 17/09/2026): "vamos criar elas em uma
           fila com todas separadas, e quem pegar, cadastrar e falar vira dono
           lead. Mais fácil."
           São 706 conversas ativas sem documento E sem lead — ~62 novas por
           dia. Elas não existem para o CRM: sem documento não há cliente, sem
           cliente não há lead, e sem lead não há proposta, pedido, carteira nem
           comissão.
           ⚠️ O LEAD TEM QUE ENTRAR NA CONTA: são 226 conversas sem documento
           MAS COM lead, que já têm cliente identificado e continuam sendo do
           dono. Filtrar só por documento vazio entregaria essas 226 junto. */
        query = query.is('lead_id', null);
        query = query.or('cliente_cnpj.is.null,cliente_cnpj.eq.');
      }
      if (tab === 'sem-dono') {
        /* SEM DONO — Douglas, 17/09/2026: a aba tem que ficar ao lado de
           Todas e Esperando.
           É a fila de quem pegar primeiro: ninguém assumiu, e com a Lei da
           Visão ela já está visível para quem precisa. Não é o mesmo que
           "sem cadastro": ali o problema é não existir documento; aqui o
           cliente pode estar inteiro no CRM e só não ter dono.
           Conversa encerrada não entra — fila é do que está em aberto. */
        /* Douglas 29/09/2026: "só deveria existir se algum cliente não tem
           vínculo com algum lead... chegou sem dono ou não foi vinculado".
           Ninguém atendendo cliente que TEM dono não entra: continua em Todas.
           A mesma régua do contador (inbox_contadores). */
        query = query
          .is('owner_user_id', null)
          .is('lead_id', null)
          .is('consultor_comercial_id', null)
          .is('chat_grupo_id', null)
          .not('status', 'in', '("finalizada","arquivada","convertida")');
      }
      if (tab === 'novas') {
        query = query
          .gt('unread_count', 0);
        if (forcarPropriasApenas && profile?.id) query = query.eq('owner_user_id', profile.id);
        // (Douglas 16/09/2026) QUEM VÊ O QUÊ É DECIDIDO NO BANCO, pela LEI DA
        // VISÃO DO INBOX (can_view_conversa_v5): dono → equipe do líder → BKO
        // pela fila do cliente → gestão vê tudo; conversa sem dono fica na fila
        // da conexão. Este hook NÃO refiltra por owner aqui, e isso é a correção:
        // o filtro antigo (`owner = eu OU sem dono`) escondia do BKO de contrato,
        // input e tramitação exatamente as conversas que a lei dá a eles — o
        // cliente com item na fila deles, que quase nunca tem owner = eles.
        // Para o consultor o resultado é idêntico ao de antes, porque a lei
        // concede justamente os dele mais os sem dono da conexão dele.
      } else if (tab === 'minhas') {
        // "Minhas" = conversas ativas (em_atendimento + aguardando_cliente).
        // Antes existia "Aguardando" separada — foi fundida em Minhas com
        // chips internos pra evitar a confusão de ter o mesmo cliente em
        // duas abas dependendo do botão clicado.
        //
        // visaoEquipe (master/gerente/diretor/qualidade/supervisor/
        // coordenador) vê TUDO — caso contrário (consultor/bko), filtra
        // por owner_user_id = me. Necessário porque gerente/qualidade NÃO
        // atende — então não tem nada com owner=ela; precisa enxergar o
        // que a equipe está atendendo.
        query = query.in('status', ['em_atendimento', 'aguardando_cliente']);
        if (!visaoEquipe) {
          query = query.eq('owner_user_id', profile?.id);
        }
      } else if (tab === 'aguardando') {
        // Compat: alias da nova aba "Minhas" para deep-links/forced-tab
        // antigos (CRMPanel "Passar Atendimento" / "Aguardando Cliente").
        // Filtra apenas o sub-grupo aguardando_cliente.
        query = query.eq('status', 'aguardando_cliente');
        if (!visaoEquipe) {
          query = query.eq('owner_user_id', profile?.id);
        }
      } else if (tab === 'finalizadas') {
        query = query.in('status', ['finalizada', 'arquivada']);
      } else if (tab === 'ia-atendendo') {
        // IA em atendimento ativo: ninguem assumiu (owner null) + campanha
        // de origem tem agente_ativo. Cobre conversas onde a IA esta
        // respondendo SEM intervencao humana.
        // Nao filtramos por agente_ativo aqui (e join, supabase-js complica) -
        // filtramos PÓS-fetch, abaixo.
        query = query
          .is('owner_user_id', null)
          .not('status', 'in', '("finalizada","arquivada","convertida")');
      } else if (tab === 'disparos') {
        // Visão "Disparos": conversas que chegaram por botão de disparo de
        // e-mail/campanha — qualquer tag de regra de roteamento com
        // tipo_aviso (aviso dourado) ou ainda aguardando atendimento.
        // A lista de tags vem do banco pra ficar configurável pela UI Filas.
        const tagsDisparo = Array.from(new Set([
          ...((regrasAviso || []) as any[]).map((r) => r.pool_destino).filter(Boolean),
          'disparo-aguardando',
        ]));
        // SEM filtro de status de proposito (Douglas 05/08): Disparos e' visao de
        // INFLUXO — serve pra controlar o que entrou hoje e o que ja entrou no todo,
        // incluindo o que ja foi finalizado/convertido. Antes escondia as fechadas e
        // a lista batia 29 contra as 167 do chip: clicava no numero e via outro.
        query = query.overlaps('tags', tagsDisparo);
      } else if (tab === 'emkt-rf') {
        // (legado) Conversas que vieram de respostas a Email Marketing dos Diários RF.
        // Mantém compat com a antiga aba hard-coded — visualmente substituída pela
        // visão dinâmica `tag:EMKT-RF` gerada pelas regras de roteamento.
        query = query
          .contains('tags', ['EMKT-RF'])
          .not('status', 'in', '("arquivada","finalizada","convertida")');
      } else if (typeof tab === 'string' && tab.startsWith('qualidade:')) {
        if (forcarPropriasApenas && profile?.id) query = query.eq('owner_user_id', profile.id);
      } else if (typeof tab === 'string' && tab.startsWith('tag:')) {
        // Visão dinâmica gerada por regra de roteamento — filtra por tag.
        const tag = tab.slice(4);
        query = query
          .contains('tags', [tag])
          .not('status', 'in', '("arquivada","finalizada","convertida")');
      } else {
        if (tab !== 'todas') query = query.not('status', 'in', '("arquivada","finalizada","convertida")');
        if (forcarPropriasApenas && profile?.id) {
          query = query.eq('owner_user_id', profile.id);
        }
        /* ⛔ NÃO filtrar "Todas" por dono (Douglas 23/09/2026): "conversa sem
           dono tem que ter visão de todos, pois é cliente que entrou e não tem
           vínculo. Então basta vincular. ISSO JÁ EXISTIA, NÃO ERA PRA TROCAR."
           A aba Sem Dono é atalho para a mesma fila, nunca o único lugar dela. */      }

      /* ── OS FILTROS DA TELA, AGORA NA CONSULTA ──────────────────────────
         ⚠️ Teto de 60 ids por lista: `.in()` monta a lista na URL e lista
         gigante ja' derrubou consulta neste CRM com 410 MUDO (cancelados,
         02/08). Passando de 60 a selecao e' larga o bastante pra nao filtrar
         quase nada — deixamos o corte pro client-side, que e' exato. */
      const TETO_IN = 60;
      const tagsSel = (filtros?.tags ?? []).filter(Boolean);
      const connSel = (filtros?.connectionIds ?? []).filter(Boolean);
      const ownerSel = (filtros?.ownerIds ?? []).filter(Boolean);
      // `contains` = TEM TODAS as tags (AND), igual ao filtro da tela.
      if (tagsSel.length) query = query.contains('tags', tagsSel);
      if (connSel.length && connSel.length <= TETO_IN) query = query.in('connection_id', connSel);
      if (ownerSel.length && ownerSel.length <= TETO_IN) query = query.in('owner_user_id', ownerSel);
      const campSel = (filtros?.campanhaIds ?? []).filter(Boolean);
      if (campSel.length && campSel.length <= TETO_IN) query = query.in('campanha_id', campSel);
      const convSel = (filtros?.conversationIds ?? []).filter(Boolean);
      if (convSel.length) query = query.in('id', convSel.slice(0, TETO_IN));
      if (filtros?.soFilas) query = query.setHeader('x-inbox-escopo', 'filas');
      else if (filtros?.soCarteira) query = query.setHeader('x-inbox-escopo', 'carteira');
      if (filtros?.segmento) query = query.setHeader('x-inbox-segmento', filtros.segmento);
      return query;
      };

      const conversasPromise = (async () => {
        if (!tab.startsWith('qualidade:')) return construirQuery().then((resultado) => resultado);
        const fila = await queryClient.fetchQuery(filaQualidadeOptions(profile?.id, forcarPropriasApenas));
        const documentos = documentosDaEtapa(fila, tab.slice(10), filtros?.competencias ?? [], filtros?.estadosEsim ?? []);
        // Lotes de 60 (o teto da URL, ver abaixo), disparados em paralelo. A RLS
        // do gateway decide o que volta — não perguntamos isso duas vezes.
        const registros = [];
        for (let offset = 0; offset < documentos.length; offset += 240) {
          const lotes = [];
          for (let inicio = offset; inicio < Math.min(offset + 240, documentos.length); inicio += 60) {
            lotes.push(construirQuery().in('cliente_cnpj', documentos.slice(inicio, inicio + 60)));
          }
          const resultados = await Promise.all(lotes);
          for (const resultado of resultados) {
            if (resultado.error) return resultado;
            registros.push(...(resultado.data || []));
          }
        }
        registros.sort((a, b) => String(b.last_message_at || '').localeCompare(String(a.last_message_at || '')) || a.id.localeCompare(b.id));
        return { data: registros, error: null };
      })();
      const gruposPromise = (async () => {
        // ── Grupos internos: query própria, simples, e mescla ordenada ──
        // Aba 'novas' é fila de cliente — grupo não entra lá. Nas demais abas
        // (todas/minhas/aguardando/finalizadas etc.) grupo aparece como
        // conversa normal, igual WhatsApp.
        let grupoRows: any[] = [];
        if (pageParam === 0 && tab !== 'novas' && tab !== 'disparos' && tab !== 'emkt-rf' && !(typeof tab === 'string' && (tab.startsWith('tag:') || tab.startsWith('qualidade:')))) {
          try {
            let gq = supabase
              .from('whatsapp_conversations')
              .select(`
                id, telefone, wa_id, destinatario_id, lead_id, cliente_cnpj, liberada_consultor, chat_grupo_id,
                owner_user_id, consultor_comercial_id, connection_id, campanha_id, status, tags, unread_count, agente_ia_atendendo, agente_ia_responsavel_id, ia_pausada_em,
                last_message_preview, last_message_at, last_message_status,
                last_message_direction, last_inbound_at, last_outbound_at,
                first_inbound_at, finalizada_em, created_at, contact_name, contact_avatar_url,
                meta_window_expires_at, meta_conversation_id, meta_conversation_origin, bee_pode_responder, bee_pode_responder_em,
                solicitacao_atendimento_de_user_id, solicitacao_atendimento_em, canal_atual
              `)
              .not('chat_grupo_id', 'is', null)
              .order('last_message_at', { ascending: false, nullsFirst: false })
              .limit(30);
            gq = tab === 'todas' ? gq : tab === 'finalizadas'
              ? gq.in('status', ['finalizada', 'arquivada'])
              : gq.not('status', 'in', '("arquivada","finalizada","convertida")');
            const { data: gData } = await gq;
            grupoRows = gData || [];
          } catch { /* grupos indisponíveis não podem derrubar a lista */ }
        }
        return grupoRows;
      })();
      const [{ data, error }, grupoRows] = await Promise.all([conversasPromise, gruposPromise]);
      if (error) throw error;
      if (grupoRows.length) {
        const idsJa = new Set((data || []).map((c: any) => c.id));
        const novos = grupoRows.filter((g: any) => !idsJa.has(g.id));
        if (novos.length) {
          (data as any[]).push(...novos);
          (data as any[]).sort((a: any, b: any) =>
            String(b.last_message_at || '').localeCompare(String(a.last_message_at || '')),
          );
        }
      }

      const seen = new Set<string>();
      const deduped: any[] = [];
      for (const row of (data || [])) {
        const key = row.id;
        if (seen.has(key)) continue;
        seen.add(key);
        deduped.push(row);
      }

      // Agente RESPONSAVEL da conversa (ex: IA do disparo) vence o agente da
      // conexao. Resolve o nome via ia_agentes (sem embed FK). Fallback: conexao.
      const respIds = Array.from(new Set(deduped.map((r: any) => r.agente_ia_responsavel_id).filter(Boolean)));
      const agentesById: Record<string, string> = {};
      if (respIds.length) {
        const { data: ags } = await supabase.from('ia_agentes').select('id, nome').in('id', respIds as string[]);
        for (const a of (ags || []) as any[]) agentesById[a.id] = a.nome;
      }
      const mapped = deduped.map((row: any) => ({
        ...row,
        owner_nome: row.owner?.nome || null,
        owner_foto: row.owner?.foto_url || null,
        owner_equipe: row.owner?.equipe?.nome || null,
        // DEPRECATED (24/05/2026): conversa nao distingue mais origem.
        // Campos mantidos no type pra retrocompat mas sempre null.
        campanha_nome: null,
        campanha_template: null,
        // IA atendendo: fonte da verdade unica e' a CONEXAO. Campanha foi
        // descontinuada (Douglas 24/05: "NAO EXISTE MAIS CAMPANHA"). Toda
        // conversa entra direto pela whatsapp_connections — cada uma com
        // seu agente_ativo + agente_ia_id. Sem fallback.
        agente_ia_ativo: !!row.connection?.agente_ativo && !!row.connection?.agente?.nome,
        agente_ia_atendendo: !!row.agente_ia_atendendo,
        ia_pausada_em: row.ia_pausada_em ?? null,
        agente_ia_nome: (row.agente_ia_responsavel_id ? agentesById[row.agente_ia_responsavel_id] : null) || row.connection?.agente?.nome || null,
        agente_ia_id: row.agente_ia_responsavel_id || row.connection?.agente?.id || row.connection?.agente_ia_id || null,
        destinatario_nome: row.destinatario?.nome || null,
        destinatario_razao_social: row.destinatario?.razao_social || null,
        destinatario_cnpj: row.destinatario?.cnpj || row.cliente_cnpj || null,
        connection_nome: row.connection?.nome || null,
        connection_telefone: row.connection?.telefone || null,
        connection_provider: row.connection?.provider || null,
        canal_atual: (row as { canal_atual?: string | null }).canal_atual || null,
        solicitante_atendimento_nome: row.solicitante_atendimento?.nome || null,
        tags: row.tags || [],
      })) as Conversation[];

      // Aba 'ia-atendendo': filtra POS-fetch porque agente_ia_ativo e derivado
      // do join com campanha (nao da pra fazer no query supabase-js direto).
      const filtradoPorTab = tab === 'ia-atendendo'
        ? mapped.filter(c => !!c.agente_ia_ativo && !c.owner_user_id)
        : mapped;
      return filtradoPorTab;
    },
    enabled: !!profile?.id,
    staleTime: 15_000,
    // Nunca exibir a aba anterior como se fossem clientes da fila de Qualidade.
    refetchInterval: pausarAtualizacao ? false : smartInterval,
  });
  const conversations = Array.from(new Map((paginas?.pages.flat() || []).map(c => [c.id, c])).values());

  // Filtragem client-side por busca. Cobre os mesmos campos que o
  // ConversationList refiltra depois (telefone com/sem máscara, CNPJ,
  // contato, destinatário, razão social, campanha, consultor, equipe,
  // agente IA, conexão, preview, tags). Ter paridade aqui evita que
  // conversas válidas sejam descartadas antes de chegar ao componente.
  const filtered = (() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return conversations;
    const qDigits = q.replace(/\D/g, '');
    const matchDigits = (raw?: string | null) =>
      !!qDigits && !!raw && raw.replace(/\D/g, '').includes(qDigits);
    return conversations.filter((c) =>
      c.telefone?.toLowerCase().includes(q) ||
      matchDigits(c.telefone) ||
      matchDigits(c.destinatario_cnpj) ||
      c.contact_name?.toLowerCase().includes(q) ||
      c.destinatario_nome?.toLowerCase().includes(q) ||
      c.destinatario_razao_social?.toLowerCase().includes(q) ||
      c.destinatario_cnpj?.toLowerCase().includes(q) ||
      // DEPRECATED 24/05/2026: busca por campanha_nome removida.
      c.owner_nome?.toLowerCase().includes(q) ||
      c.owner_equipe?.toLowerCase().includes(q) ||
      c.agente_ia_nome?.toLowerCase().includes(q) ||
      c.connection_nome?.toLowerCase().includes(q) ||
      c.last_message_preview?.toLowerCase().includes(q) ||
      (Array.isArray(c.tags) && c.tags.some(t => t.toLowerCase().includes(q)))
    );
  })();

  // Supabase Realtime — atualização cirúrgica via setQueryData.
  //
  // Antes: cada UPDATE (mesmo de unread_count) invalidava a query inteira
  // e forçava refetch das 100 conversas com 4 joins. Isso causava a sensação
  // de "salto" na lista a cada mensagem nova.
  //
  // Agora: UPDATE atualiza só os campos da linha afetada direto no cache
  // (sem refetch). INSERT/DELETE caem em invalidateQueries debounced, porque
  // dependem de reconstruir joins (owner, campanha, destinatario, connection)
  // que só vêm da query original.
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lentoRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const meuIdRef = useRef<string | undefined>(profile?.id);
  meuIdRef.current = profile?.id;
  // Ficou algo para tras enquanto a aba estava escondida? O `onVis` cobra.
  const pendenteRef = useRef(false);
  useEffect(() => {
    // Fix realtime lista (20/06): a atualizacao cirurgica mexia no cache mas
    // nao re-renderizava a lista de forma confiavel — e pulava o refetch. Os
    // eventos CHEGAM no navegador (provado: supabase-js recebe os UPDATE de
    // whatsapp_conversations). Entao a cada evento => refetch debounced (350ms),
    // garantindo que a lista reordene na hora.
    /* ⛔ ABA QUE NINGUEM ESTA OLHANDO NAO REFAZ A LISTA (21/09/2026).
     *
     * O debounce de 2,5 s parece um freio, mas nao e': o Inbox recebe mensagem
     * o tempo todo, e cada mensagem e' um UPDATE em whatsapp_conversations.
     * Na pratica a lista se refazia A CADA 2,5 s, para sempre, em TODA aba
     * aberta — nao importa se alguem estava olhando.
     *
     * Medido em 21/09: 7.511 refetches desta lista em 33 min, um a cada 2,9 s
     * por aba (bate com o debounce). E o heartbeat de presenca revelou ~80
     * ABAS abertas para 11 pessoas no Inbox: cada um deixa varias abas, e todas
     * as escondidas estavam refazendo a lista inteira, com a lei de visao
     * aplicada, para ninguem ver.
     *
     * O polling ja' pausava sozinho com a aba escondida (useSmartPolling olha
     * visibilityState). O realtime era o furo: nao olhava nada.
     *
     * Aqui a aba escondida apenas ANOTA que ficou para tras; o `onVis` abaixo
     * cobra a atualizacao no instante em que ela volta a aparecer. Quem esta'
     * olhando continua com os mesmos 2,5 s de antes — a experiencia de quem usa
     * nao muda em nada, so' para de haver trabalho para plateia nenhuma.
     */
    /* ⚡ SÓ A CONVERSA QUE ME DIZ RESPEITO REFAZ A LISTA EM 2,5 s (08/10/2026).
     *
     * O canal escuta a tabela inteira: cada mensagem de QUALQUER conversa do
     * CRM refazia a lista de todo mundo que estava olhando o Inbox, e cada
     * lista aplica a lei de visão sobre ~40 mil conversas (0,5 a 1 s no banco).
     * Agora: conversa que já está na minha lista, que é minha ou que acabou de
     * nascer → 2,5 s, como antes. Mudança em conversa de outra pessoa → a lista
     * se confere em até 30 s (é assim que aparece o cliente meu que escreveu
     * numa conversa antiga, fora das páginas carregadas). */
    type EventoConversa = { eventType?: string; new?: { id?: string; owner_user_id?: string | null; consultor_comercial_id?: string | null } };
    const relevante = (payload: EventoConversa) => {
      if (!payload || payload.eventType !== 'UPDATE') return true;
      const nova = payload.new || {};
      const eu = meuIdRef.current;
      if (eu && (nova.owner_user_id === eu || nova.consultor_comercial_id === eu)) return true;
      const id = nova.id;
      if (!id) return true;
      return queryClient.getQueriesData<{ pages?: Conversation[][] }>({ queryKey: ['whatsapp-conversations'] })
        .some(([, d]) => (d?.pages || []).some(pg => Array.isArray(pg) && pg.some(c => c?.id === id)));
    };
    const disparar = () => {
      if (debounceRef.current) { clearTimeout(debounceRef.current); debounceRef.current = null; }
      if (lentoRef.current) { clearTimeout(lentoRef.current); lentoRef.current = null; }
      queryClient.invalidateQueries({ queryKey: ['whatsapp-conversations'] }, { cancelRefetch: false });
    };
    const bump = (payload?: EventoConversa) => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
        pendenteRef.current = true;
        return;
      }
      if (payload === undefined || relevante(payload)) {
        if (debounceRef.current) return;
        debounceRef.current = setTimeout(disparar, 2500);
        return;
      }
      if (lentoRef.current || debounceRef.current) return;
      lentoRef.current = setTimeout(disparar, 30_000);
    };
    let canal: any = null;
    let vivo = true;
    let jaEntrou = false;
    const assinar = () => {
      if (!vivo) return;
      if (canal) { try { supabase.removeChannel(canal); } catch { /* noop */ } }
      jaEntrou = false;
      canal = supabase
        .channel('whatsapp-conversations-realtime')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'whatsapp_conversations' }, bump)
        .subscribe((status: string) => {
          if (status !== 'SUBSCRIBED') return;
          if (jaEntrou) bump();
          jaEntrou = true;
        });
    };
    assinar();

    // WS cai quando o backend reinicia e o canal NAO volta sozinho — watchdog
    // re-assina e forca um refetch pra recuperar o que mudou no gap.
    const watchdog = setInterval(() => {
      const st = String(canal?.state || '');
      if (st !== 'joined' && st !== 'joining') { assinar(); bump(); }
    }, 15_000);
    // Voltou a aparecer: se ficou algo para tras enquanto estava escondida,
    // atualiza na hora — sem esperar o debounce, porque a pessoa esta' olhando
    // agora. `pendenteRef` e' o que garante que nada se perde no caminho.
    const onVis = () => {
      if (document.visibilityState !== 'visible') return;
      if (pendenteRef.current) {
        pendenteRef.current = false;
        queryClient.invalidateQueries({ queryKey: ['whatsapp-conversations'] }, { cancelRefetch: false });
        return;
      }
      bump();
    };
    document.addEventListener('visibilitychange', onVis);

    return () => {
      vivo = false;
      clearInterval(watchdog);
      document.removeEventListener('visibilitychange', onVis);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (lentoRef.current) clearTimeout(lentoRef.current);
      if (canal) supabase.removeChannel(canal);
    };
  }, [queryClient]);

  return { conversations: filtered, isLoading, isFetching, refetch, hasNextPage, fetchNextPage, isFetchingNextPage };
}

// IDs das campanhas "Apenas IA" (ia_only) — alimenta o chip "Só Master" no
// inbox (lista + header do chat). Só master enxerga essas conversas, então o
// chip na prática só aparece pra ele.
export function useCampanhasIaOnly() {
  return useQuery({
    queryKey: ['campanhas-ia-only'],
    staleTime: 60_000,
    queryFn: async () => {
      const { data } = await supabase
        .from('whatsapp_campanhas')
        .select('id')
        .eq('visibilidade', 'ia_only');
      return new Set(((data as any[]) || []).map((c) => c.id as string));
    },
  });
}

// Badge count para sidebar
/**
 * ⚠️ SEM USO HOJE (conferido em 21/09/2026): nenhum componente importa este
 * hook — quem mostra o badge de nao-lidas no menu e' o `AppSidebar`, com a sua
 * propria consulta. Ficou aqui porque a regua dele (nao refiltrar por owner,
 * deixando a LEI DA VISAO decidir) e' a correta e custou um bug para acertar:
 * refiltrar por dono zerava o badge do BKO, que enxerga por FILA e nao por
 * posse. Quem for unificar os dois deve partir DESTA regua, nao da do Sidebar.
 *
 * Por nao ter quem o chame, nao custa nada rodando — mas o `count: 'exact'`
 * que ele carregava foi removido mesmo assim, para ninguem copiar o padrao.
 */
export function useWhatsappInboxUnreadCount() {
  const { profile } = useAuth();
  
  return useQuery({
    queryKey: ['whatsapp-inbox-unread-count', profile?.id],
    queryFn: async () => {
      if (!profile?.id) return 0;
      // Roles que veem tudo (via RLS): master, gerente, diretor
      // Roles que veem equipe (via RLS): supervisor, coordenador
      // BKO, qualidade, consultor: só as suas
      const rolesVisaoAmpla = ['master', 'gerente', 'diretor', 'supervisor', 'coordenador'];
      const temVisaoAmpla = rolesVisaoAmpla.includes(profile.role || '');

      /* ⚠️ SEM `count: 'exact'` (21/09/2026). Ele estava aqui e o resultado era
       * JOGADO FORA: quem soma as nao-lidas e' o reduce abaixo, sobre as linhas.
       * O `count` exato manda o Postgres varrer as 31 mil conversas aplicando a
       * lei de visao linha a linha, so' para devolver um numero que ninguem le. */
      const query = supabase
        .from('whatsapp_conversations')
        .select('unread_count')
        .gt('unread_count', 0)
        .not('status', 'in', '("finalizada","arquivada")');

      // Sem refiltrar por owner: o banco já entregou só o que este usuário pode
      // ver (LEI DA VISÃO). Refiltrar aqui zerava o badge de não-lidas do BKO,
      // que enxerga por FILA e não por posse.

      const { data } = await query;
      return (data || []).reduce((sum: number, c: any) => sum + (c.unread_count || 0), 0);
    },
    enabled: !!profile?.id,
    staleTime: 30_000,
    refetchInterval: 30_000,
  });
}
